import { Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { firstValueFrom, map, of } from "rxjs";
import { HandleValidatorService } from "src/app/core/services/handle-validator.service";
import { FormlyAvatarModule } from "src/app/core/ui/formly-avatar/formly-avatar.module";
import { FormlyBannerModule } from "src/app/core/ui/formly-banner/formly-banner.module";
import { UserProfile } from "src/app/subscription/services/auth/auth.interface";
import { AuthService } from "src/app/subscription/services/auth/auth.service";

type HeaderFormModel = Pick<
  UserProfile,
  "display_name" | "avatar_url" | "handle" | "tagline" | "banner_url"
>;

@Component({
  selector: "app-classroom-header-modal",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    FormlyAvatarModule,
    FormlyBannerModule,
  ],
  template: `
    @if (model(); as model) {
      <div class="modal-header">
        <h4 class="modal-title">Edit Page Header</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modalRef.dismiss()"></button>
      </div>

      <form [formGroup]="form" (ngSubmit)="save()">
        <div class="modal-body">
          @if (error) {
            <div class="alert alert-danger" role="alert">{{ error }}</div>
          }
          <formly-form [form]="form" [fields]="formFields" [model]="model"></formly-form>
        </div>

        <div class="modal-footer d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-dark" (click)="modalRef.dismiss()">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="!form.valid || isLoading">
            @if (isLoading) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            }
            Save
          </button>
        </div>
      </form>
    }
  `,
})
export class ClassroomHeaderModalComponent {
  private authService = inject(AuthService);
  private handleValidator = inject(HandleValidatorService);
  modalRef = inject(NgbActiveModal);

  form = new FormGroup({});
  isLoading = false;
  error?: string;

  private user = toSignal(this.authService.$user, { initialValue: null });

  model = computed(() => {
    const user = this.user();
    if (!user) return null;
    return {
      display_name: user.display_name ?? null,
      avatar_url: user.avatar_url ?? null,
      handle: user.handle ?? null,
      tagline: user.tagline ?? null,
      banner_url: user.banner_url ?? null,
    };
  });

  formFields: FormlyFieldConfig[] = [
    {
      key: "banner_url",
      type: "banner",
      props: {
        label: "Banner",
        description: "Recommended size: 1200x400 pixels",
      },
    },
    {
      fieldGroupClassName: "row mt-3",
      fieldGroup: [
        {
          className: "col-12 col-md-5",
          key: "avatar_url",
          type: "avatar",
          props: {
            label: "Avatar",
            leftAlign: true,
            square: true,
          },
        },
        {
          className: "col-12 col-md-7",
          fieldGroup: [
            {
              key: "display_name",
              type: "input",
              props: {
                label: "Display Name",
                placeholder: "e.g., Mrs. Smith",
                description: "How you want to be addressed",
                required: true,
              },
            },
            {
              key: "handle",
              type: "input",
              props: {
                label: "Page URL",
                placeholder: "e.g., mrssmith",
                description: "Your custom URL: classjax.com/u/your-handle",
              },
              modelOptions: {
                debounce: { default: 400 },
              },
              validators: {
                handle: {
                  expression: (control: { value: string }) => {
                    if (!control.value) return true;
                    const value = control.value.toLowerCase();
                    return /^[a-z0-9][a-z0-9-]{2,22}[a-z0-9]$/.test(value) && !value.includes("--");
                  },
                  message: "Use 4-24 lowercase letters, numbers, and hyphens.",
                },
              },
              asyncValidators: {
                handleAvailable: {
                  expression: (control: AbstractControl) => {
                    if (!control.value) return of(true);
                    const value = control.value.toLowerCase();
                    // Skip validation if handle unchanged from original
                    const originalHandle = this.user()?.handle?.toLowerCase();
                    if (value === originalHandle) return of(true);
                    if (!/^[a-z0-9][a-z0-9-]{2,22}[a-z0-9]$/.test(value) || value.includes("--")) {
                      return of(true);
                    }
                    return this.handleValidator.checkHandle(value).pipe(
                      map((response) => {
                        if (!response.available && response.reason) {
                          (control as AbstractControl & { _handleError?: string })._handleError =
                            this.handleValidator.getErrorMessage(response.reason);
                        }
                        return response.available;
                      }),
                    );
                  },
                  message: (_error: unknown, field: FormlyFieldConfig) => {
                    const control = field.formControl as AbstractControl & { _handleError?: string };
                    return control._handleError || "This handle is not available";
                  },
                },
              },
            },
            {
              key: "tagline",
              type: "input",
              props: {
                label: "Tagline",
                placeholder: "e.g., 6th Grade English / Reading",
                description: "A short description that appears below your name",
              },
            },
          ],
        },
      ],
    },
  ];

  async save() {
    if (!this.form.valid) return;

    this.isLoading = true;
    this.error = undefined;

    const user = this.user();
    if (!user) {
      this.error = "Not logged in";
      this.isLoading = false;
      return;
    }

    const formData = this.form.value as HeaderFormModel;

    const profileData: Partial<UserProfile> = {
      banner_url: formData.banner_url || undefined,
      avatar_url: formData.avatar_url || undefined,
      display_name: formData.display_name || undefined,
      handle: formData.handle ? formData.handle.toLowerCase().trim() : null,
      tagline: formData.tagline || undefined,
    };

    try {
      await firstValueFrom(this.authService.updateUserProfile(user.id, profileData));
      this.modalRef.close(true);
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : "Failed to save";
    } finally {
      this.isLoading = false;
    }
  }
}
